import React from 'react';
import PostalBubbleMap from './postal-bubble-map';

// All Markets Snapshot map (UX note §58b): one map framing every market —
// a labeled halo per MARKET at its center (markets carry lat/lng on the
// /v1/location rows), sized by the selected metric. Reads as "where our
// markets are and which are hot", not raw traffic dots. Clicking a market
// opens its market page landing on the §58 postal-code view (we pre-set
// the per-market view choice before navigating). Together they form the
// zoom ladder: All Markets (country) → Market (postal codes) → row
// detail. No new endpoints — locations + the placeIndex the snapshot
// already uses.
const METRIC_TABS = [['views', 'Views'], ['sessions', 'Sessions'], ['conversions', 'Conversions']];
const n = (r, k) => Number(r && (r[k] ?? r[k.charAt(0).toUpperCase() + k.slice(1)])) || 0;

// Number.isFinite alone passes ungeocoded/mis-geocoded rows — the (0,0) null
// island especially, which drags a market's centroid out to sea and stretches
// the bbox. World markets ARE supported now (PostalBubbleMap swaps to the world
// basemap when the view spans beyond the US), so accept any real global coord
// and reject only the null-island and out-of-range junk.
const validCoord = (lat, lng) => {
  lat = Number(lat);
  lng = Number(lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -60 || lat > 84) return false; // Antarctica / N-pole junk
  if (lng < -180 || lng > 180) return false;
  if (Math.abs(lat) < 1 && Math.abs(lng) < 1) return false; // (0,0) null island
  return true;
};
export default function AllMarketsMap({
  markets,
  locations,
  placeIndex,
  marketData,
  onOpenMarket
}) {
  const [metric, setMetric] = React.useState('views');
  const rows = React.useMemo(() => {
    const locByName = {};
    (locations || []).forEach(l => {
      const name = l.name || l.LocationName;
      if (name) locByName[String(name).toLowerCase()] = l;
    });
    // Market center: the location row's lat/lng when geocoded; else the
    // centroid of the market's own postal rows (some tenants' location
    // records carry null lat/lng — Fairfield does; the traffic data
    // still knows where the market is).
    const centerOf = m => {
      const loc = locByName[String(m).toLowerCase()];
      if (loc && validCoord(loc.lat, loc.lng)) {
        return {
          lat: Number(loc.lat),
          lng: Number(loc.lng)
        };
      }
      const zips = (((marketData || {})[m] || {}).placeZips || []).filter(z => validCoord(z.lat, z.lng));
      if (!zips.length) return null;
      return {
        lat: zips.reduce((a, z) => a + Number(z.lat), 0) / zips.length,
        lng: zips.reduce((a, z) => a + Number(z.lng), 0) / zips.length
      };
    };
    return (markets || []).map(m => {
      const c = centerOf(m);
      if (!c) return null;
      return {
        lat: c.lat,
        lng: c.lng,
        label: m,
        sub: null,
        value: n((placeIndex || {})[m], metric)
      };
    }).filter(Boolean);
  }, [markets, locations, placeIndex, marketData, metric]);
  if (rows.length === 0) return null;
  return /*#__PURE__*/React.createElement("section", {
    className: "mi-panel mi-panel-full",
    "aria-label": "All Markets map"
  }, /*#__PURE__*/React.createElement("header", {
    className: "mi-panel-header"
  }, /*#__PURE__*/React.createElement("h2", null, "Your markets"), /*#__PURE__*/React.createElement("div", {
    className: "mi-toggle-group"
  }, METRIC_TABS.map(([key, label]) => /*#__PURE__*/React.createElement("button", {
    key: key,
    type: "button",
    className: `mi-sort-tab ${metric === key ? 'active' : ''}`,
    onClick: () => setMetric(key)
  }, label)))), /*#__PURE__*/React.createElement("div", {
    className: "mi-panel-body"
  }, /*#__PURE__*/React.createElement(PostalBubbleMap, {
    rows: rows,
    showLabels: true,
    height: 380,
    onClick: label => {
      // land the market page on the postal view (§58b)
      try {
        localStorage.setItem(`plunk-mi-geoview-${label}`, 'postal');
      } catch (e) {/* private mode */}
      onOpenMarket && onOpenMarket(label);
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "mi-report-meta",
    style: {
      marginTop: 6
    }
  }, "Halo size = ", metric, " in the selected range. Click a market to open it.")));
}